/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Toast } from 'sharedUi/Toast';
import PageSkeleton from '../../components/common/page-skeleton';
import ScreenHeader from '../../components/common/screen-header';
import { InputNumber } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'sharedUi/Button';
import { Input } from 'sharedUi/Input';
import { TextArea } from 'sharedUi/TextArea';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import FormSelect from '../../components/forms/form-select';
import { useAccessibleProjects, projectFieldOptions, appIdFromField, defaultProjectId, SHARED_PROJECT } from '../../hooks/useProjectScope';
import { notificationTypesService } from '../../services/notification-types.service';
import { valueSetsService } from '../../services/value-sets.service';
import { extractErrorMessage } from '../../utils/error-handler';

const schema = z.object({
  // The backend's own rule (CODE_PATTERN, ^[A-Z0-9_]{2,64}$), checked here so a bad
  // code is caught at the field, as it is typed — not after Save, as a validation error
  // naming a regular expression.
  // Empty means shared; the API honours this only inside the caller's scope.
  // Required: a blank appId is what the API reads as "shared with every
  // project", so an unset field would quietly author a global row.
  appId: z.string().min(1, 'Choose the project this belongs to.'),
  notificationCode: z.string()
    .min(1, 'Notification code is required')
    .regex(/^[A-Z0-9_]{2,64}$/,
      'Use upper-case letters, digits and underscores only — 2 to 64 characters.'),
  category: z.string().min(1, 'Category is required'),
  priority: z.string().optional().or(z.literal('')),
  channels: z.array(z.string()).min(1, 'At least one channel — a type with none is undeliverable'),
  description: z.string().max(1000).optional().or(z.literal('')),
  isChannelFallbackEnabled: z.boolean().optional(),
  expiresAfterSeconds: z.number().nullable().optional(),
  retentionDays: z.number().nullable().optional(),
  slaMinutes: z.number().nullable().optional(),
  isUserOptOutAllowed: z.boolean().optional(),
  isQuietHoursRespected: z.boolean().optional(),
  retryBackoff: z.string().optional().or(z.literal('')),
  retryIntervalSeconds: z.number().nullable().optional(),
});
type FormData = z.infer<typeof schema>;

const _vsOpts = (vs: any) => (vs?.items || []).map((i: any) => ({ value: i.code, label: i.label }));

/** A labelled checkbox, since the type carries six independent policy switches. */
const Switch = ({ label, hint, value, onChange, testId }: {
  label: string; hint?: string; value?: boolean; onChange: (v: boolean) => void; testId: string;
}) => (
  <div className="flex items-start gap-2">
    <Checkbox inputId={testId} checked={!!value} onChange={(e) => onChange(!!e.checked)} />
    <label htmlFor={testId} className="text-sm text-gray-700 dark:text-gray-300 leading-tight">
      {label}
      {hint ? <span className="block text-xs text-gray-400">{hint}</span> : null}
    </label>
  </div>
);

const NotificationTypeForm = () => {
  const { id, isReady, navigateToList } = useSecureNavigation({ entity: 'notification type', basePath: '/notification-types' });
  const isEdit = Boolean(id);
  const { projects, canScopeByProject } = useAccessibleProjects();
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      appId: defaultProjectId(projects),
      notificationCode: '', category: '', priority: 'NORMAL', channels: [], description: '',
      isChannelFallbackEnabled: false, expiresAfterSeconds: null, retentionDays: 365,
      slaMinutes: null, isUserOptOutAllowed: true, isQuietHoursRespected: true,
      retryBackoff: 'EXPONENTIAL', retryIntervalSeconds: null,
    },
    mode: 'onChange',
  });


  const { data: catVS } = useQuery({ queryKey: ['vs', 'notificationCategory'], queryFn: () => valueSetsService.getByKey('notificationCategory', 'en'), staleTime: 3e5 });
  const { data: prioVS } = useQuery({ queryKey: ['vs', 'notificationPriority'], queryFn: () => valueSetsService.getByKey('notificationPriority', 'en'), staleTime: 3e5 });
  const { data: chanVS } = useQuery({ queryKey: ['vs', 'notificationChannel'], queryFn: () => valueSetsService.getByKey('notificationChannel', 'en'), staleTime: 3e5 });

  const { data: typeData, isLoading } = useQuery({ queryKey: ['notificationType', id], queryFn: () => notificationTypesService.getById(id!), enabled: isEdit && isReady && !!id });

  useEffect(() => {
    if (typeData && isEdit) {
      reset({
        appId: typeData.appId || SHARED_PROJECT,
        notificationCode: typeData.notificationCode || '', category: typeData.category || '',
        priority: typeData.priority || 'NORMAL', channels: typeData.channels || [],
        description: typeData.description || '',
        isChannelFallbackEnabled: !!typeData.isChannelFallbackEnabled,
        expiresAfterSeconds: typeData.expiresAfterSeconds ?? null,
        retentionDays: typeData.retentionDays ?? 365,
        slaMinutes: typeData.slaMinutes ?? null,
        isUserOptOutAllowed: typeData.isUserOptOutAllowed !== false,
        isQuietHoursRespected: typeData.isQuietHoursRespected !== false,
        retryBackoff: typeData.retryPolicy?.backoff || 'EXPONENTIAL',
        retryIntervalSeconds: typeData.retryPolicy?.intervalSeconds ?? null,
      });
    }
  }, [typeData, isEdit, reset]);

  const mutation = useMutation({
    mutationFn: (data: any) => (isEdit && id ? notificationTypesService.update(id, data) : notificationTypesService.create(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notificationTypes'] });
      toast.current?.show({ severity: 'success', summary: 'Success', detail: `Notification type ${isEdit ? 'updated' : 'created'}`, life: 3000 });
      setTimeout(() => navigateToList(), 800);
    },
    onError: (e: any) => toast.current?.show({ severity: 'error', summary: 'Error', detail: extractErrorMessage(e, 'Save failed'), life: 10000 }),
  });

  const onSubmit = (d: FormData) => mutation.mutate({
    // Create only: the update schema has no appId, so sending one on edit
    // would look like a move that silently did not happen.
    ...(isEdit ? {} : { appId: appIdFromField(d.appId) }),
    notificationCode: d.notificationCode, category: d.category, priority: d.priority || null,
    channels: d.channels, description: d.description || null,
    isChannelFallbackEnabled: !!d.isChannelFallbackEnabled,
    expiresAfterSeconds: d.expiresAfterSeconds ?? null,
    retentionDays: d.retentionDays ?? null,
    slaMinutes: d.slaMinutes ?? null,
    isUserOptOutAllowed: d.isUserOptOutAllowed !== false,
    isQuietHoursRespected: d.isQuietHoursRespected !== false,
    // Pacing only - the API rejects an attempt cap. Sent only when it says
    // something: an empty object leaves the engine on its own defaults.
    retryPolicy: (d.retryBackoff || d.retryIntervalSeconds)
      ? {
          ...(d.retryBackoff ? { backoff: d.retryBackoff } : {}),
          ...(d.retryIntervalSeconds ? { intervalSeconds: d.retryIntervalSeconds } : {}),
        }
      : {},
  });

  if (isEdit && isLoading) return <PageSkeleton />;

  return (
    <div className="p-4" data-testid="SCR-NotificationType-Form">
      <Toast ref={toast} position="top-right" />
      <ScreenHeader icon="pi pi-bell" title={isEdit ? 'Edit Notification Type' : 'Create Notification Type'} onBack={() => navigateToList()} />
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 max-w-5xl w-full">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {canScopeByProject && (
            <Controller name="appId" control={control} render={({ field }) => (
              <FormSelect label="Project" name={field.name} value={field.value || ''}
                options={projectFieldOptions(projects, field.value)}
                onChange={field.onChange} disabled={isEdit}
                helperText={isEdit
                  ? 'Set when this was created; the API does not move one afterwards.'
                  : 'Which project owns this. It cannot be moved afterwards.'} />
            )} />
          )}
          <Controller name="notificationCode" control={control} render={({ field }) => (
            <div className="flex flex-col gap-1">
              <Input
                label="Notification code"
                required
                {...field}
                // Upper-cased as it is typed, and a space or hyphen becomes the
                // underscore the rule wants — so the common slip is corrected
                // rather than reported.
                onChange={(v: string) => field.onChange(v.toUpperCase().replace(/[\s-]+/g, '_'))}
                error={errors.notificationCode?.message}
                placeholder="GRIEVANCE_ESCALATION"
                disabled={isEdit}
              />
              {!errors.notificationCode && (
                <small className="text-xs text-gray-500 dark:text-gray-400">
                  {isEdit
                    ? 'The code cannot change — notifications and schedules refer to it.'
                    : 'Upper case, digits and underscores. Cannot be changed later.'}
                </small>
              )}
            </div>
          )} />
          <Controller name="category" control={control} render={({ field }) => (
            <FormSelect label="Category" required name={field.name} value={field.value || ''} options={_vsOpts(catVS)} onChange={field.onChange} error={!!errors.category} helperText={errors.category?.message} />
          )} />
          <Controller name="priority" control={control} render={({ field }) => (
            <FormSelect label="Priority" name={field.name} value={field.value || ''} options={_vsOpts(prioVS)} onChange={field.onChange} />
          )} />
          <Controller name="channels" control={control} render={({ field }) => (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Channels<span className="ml-1 text-red-500">*</span></label>
              <MultiSelect value={field.value} options={_vsOpts(chanVS)} onChange={(e) => field.onChange(e.value)}
                optionLabel="label" optionValue="value" display="chip" placeholder="Select channels" className="w-full" />
              {errors.channels && <small className="text-red-500">{errors.channels.message}</small>}
            </div>
          )} />
          <Controller name="description" control={control} render={({ field }) => (
            <div className="sm:col-span-2"><TextArea label="Description" {...field} rows={2} /></div>
          )} />
        </div>

        <div className="ds-card p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <h2 className="sm:col-span-2 text-sm font-semibold text-ink">
            Delivery policy
            <span className="block text-xs font-normal text-gray-400">Every notification of this type inherits these — change them here and they all follow.</span>
          </h2>
          <Controller name="slaMinutes" control={control} render={({ field }) => (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">SLA (minutes)</label>
              <InputNumber value={field.value ?? null} onValueChange={(e) => field.onChange(e.value ?? null)} min={1} className="w-full" placeholder="No deadline" />
              <small className="text-xs text-gray-400">How long a recipient has to act before the delivery is in breach.</small>
            </div>
          )} />
          <Controller name="expiresAfterSeconds" control={control} render={({ field }) => (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Expires after (seconds)</label>
              <InputNumber value={field.value ?? null} onValueChange={(e) => field.onChange(e.value ?? null)} min={1} className="w-full" placeholder="Never" />
            </div>
          )} />
          <Controller name="retentionDays" control={control} render={({ field }) => (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Retention (days)</label>
              <InputNumber value={field.value ?? null} onValueChange={(e) => field.onChange(e.value ?? null)} min={0} className="w-full" />
            </div>
          )} />
          <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Retry pacing</label>
              <span className="block text-xs text-gray-400">
                How long to wait between tries. There is no attempt limit — retrying stops once the
                message is no longer wanted, and a permanent failure is not retried at all.
              </span>
            </div>
            <Controller name="retryBackoff" control={control} render={({ field }) => (
              <FormSelect
                label="Backoff" name={field.name} value={field.value || 'EXPONENTIAL'}
                options={[
                  { value: 'EXPONENTIAL', label: 'Exponential - each wait doubles' },
                  { value: 'FIXED', label: 'Fixed - the same wait every time' },
                ]}
                onChange={field.onChange}
              />
            )} />
            <Controller name="retryIntervalSeconds" control={control} render={({ field }) => (
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">First wait (seconds)</label>
                <InputNumber value={field.value ?? null} onValueChange={(e) => field.onChange(e.value ?? null)} min={1} className="w-full" placeholder="60" />
                <small className="text-xs text-gray-400">Waits are capped at 6 hours, so a long outage settles at four tries a day.</small>
              </div>
            )} />
          </div>
          <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            <Controller name="isChannelFallbackEnabled" control={control} render={({ field }) => (
              <Switch label="Channel fallback" hint="A failed channel tries its configured backup." value={field.value} onChange={field.onChange} testId="type-fallback" />
            )} />
            <Controller name="isQuietHoursRespected" control={control} render={({ field }) => (
              <Switch label="Respect quiet hours" value={field.value} onChange={field.onChange} testId="type-quiet" />
            )} />
            <Controller name="isUserOptOutAllowed" control={control} render={({ field }) => (
              <Switch label="Allow opt-out" hint="Untick to ignore a person's own opt-out. Privileged and audited." value={field.value} onChange={field.onChange} testId="type-optout" />
            )} />
          </div>
        </div>


        <div className="flex justify-end gap-2">
          <Button type="button" label="Cancel" severity="secondary" outlined onClick={() => navigateToList()} />
          <Button type="submit" label={mutation.isPending ? 'Saving…' : isEdit ? 'Update' : 'Create'} icon={mutation.isPending ? 'pi pi-spin pi-spinner' : 'pi pi-check'} disabled={mutation.isPending} />
        </div>
      </form>
    </div>
  );
};

export default NotificationTypeForm;
